import React, { Children } from 'react';
import { TrendingUp, Home, Clock, DollarSign, Users, Wallet, Sun, Train, Building, Map, RefreshCw } from 'lucide-react';
import { motion } from 'framer-motion';
import { realEstateStats, neighborhoodStats, LAST_UPDATED, SOURCE } from '../data/marketData';
// Maps the plain string keys in data/marketData.ts to lucide icon components,
// so the data file stays editable without touching imports or JSX.
const iconMap = {
  price: DollarSign,
  sqft: TrendingUp,
  clock: Clock,
  home: Home,
  people: Users,
  money: Wallet,
  transit: Train,
  sun: Sun
} as const;
export function MarketStats() {
  const containerVariants = {
    hidden: {
      opacity: 0
    },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.15
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: 0,
      y: 30
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        ease: 'easeOut'
      }
    }
  };
  return <section id="market-stats" className="py-24 bg-white relative overflow-hidden" data-id="element-264">
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-full max-w-3xl h-px bg-gradient-to-r from-transparent via-brand-gold/30 to-transparent" data-id="element-265"></div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-266">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true,
        margin: '-100px'
      }} transition={{
        duration: 0.8
      }} className="text-center max-w-3xl mx-auto mb-10" data-id="element-267">
          <h2 className="text-sm font-bold tracking-widest text-brand-gold uppercase mb-3" data-id="element-268">
            Comprehensive Area Data
          </h2>
          <h3 className="text-3xl md:text-5xl font-serif text-gray-900 mb-6" data-id="element-269">
            Living in 91311
          </h3>
          <p className="text-gray-600 text-lg leading-relaxed" data-id="element-270">
            Chatsworth offers a unique blend of suburban tranquility, equestrian
            roots, and stunning natural landscapes in the northwestern San
            Fernando Valley. Here is everything you need to know about the local
            market and community.
          </p>
        </motion.div>

        {/* Data freshness stamp — pulled from data/marketData.ts */}
        <motion.div initial={{
        opacity: 0
      }} whileInView={{
        opacity: 1
      }} viewport={{
        once: true
      }} className="flex flex-col sm:flex-row items-center justify-center gap-x-3 gap-y-1 mb-16 text-center" data-id="element-271">
          <span className="inline-flex items-center text-xs font-bold tracking-widest uppercase text-brand-gold bg-brand-gold/10 px-3 py-1 rounded-full" data-id="element-272">
            <RefreshCw className="w-3 h-3 mr-1.5" data-id="element-273" />
            Updated {LAST_UPDATED}
          </span>
          <span className="text-xs text-gray-400" data-id="element-274">Source: {SOURCE}</span>
        </motion.div>

        {/* Real Estate Market Stats */}
        <div className="mb-16" data-id="element-275">
          <h4 className="text-xl font-serif text-gray-900 mb-8 flex items-center" data-id="element-276">
            <Building className="w-5 h-5 mr-3 text-brand-gold" data-id="element-277" />
            Real Estate Market Snapshot
          </h4>
          <motion.div variants={containerVariants} initial="hidden" whileInView="visible" viewport={{
          once: true,
          margin: '-50px'
        }} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8" data-id="element-278">
            {realEstateStats.map((stat, index) => {
            const Icon = iconMap[stat.icon];
            return <motion.div key={index} variants={itemVariants} whileHover={{
              y: -8,
              transition: {
                duration: 0.2
              }
            }} className="bg-gray-50 rounded-3xl p-8 border border-gray-100 hover:border-brand-gold/40 hover:shadow-xl hover:shadow-brand-gold/5 transition-all group relative overflow-hidden" data-id="element-279">
                  <div className="absolute top-0 right-0 w-32 h-32 bg-brand-gold/5 rounded-full blur-2xl -mr-10 -mt-10 transition-opacity group-hover:opacity-100 opacity-0" data-id="element-280"></div>

                  <div className="w-14 h-14 bg-white rounded-2xl flex items-center justify-center shadow-sm mb-6 group-hover:scale-110 transition-transform duration-300 relative z-10" data-id="element-281">
                    <Icon className="w-6 h-6 text-brand-gold" data-id="element-282" />
                  </div>
                  <p className="text-sm font-medium text-gray-500 mb-2 relative z-10" data-id="element-283">
                    {stat.label}
                  </p>
                  <div className="flex items-baseline space-x-3 mb-3 relative z-10" data-id="element-284">
                    <span className="text-4xl font-serif text-gray-900" data-id="element-285">
                      {stat.value}
                    </span>
                    <span className="text-xs font-bold tracking-wide text-green-700 bg-green-100/80 px-2.5 py-1 rounded-full" data-id="element-286">
                      {stat.trend}
                    </span>
                  </div>
                  <p className="text-sm text-gray-400 leading-relaxed relative z-10" data-id="element-287">
                    {stat.description}
                  </p>
                </motion.div>;
          })}
          </motion.div>
        </div>

        {/* Neighborhood Profile (Bento Grid) */}
        <div data-id="element-288">
          <h4 className="text-xl font-serif text-gray-900 mb-8 flex items-center" data-id="element-289">
            <Map className="w-5 h-5 mr-3 text-brand-gold" data-id="element-290" />
            Neighborhood Profile
          </h4>
          <motion.div variants={containerVariants} initial="hidden" whileInView="visible" viewport={{
          once: true,
          margin: '-50px'
        }} className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-291">
            {neighborhoodStats.map((sectionData, idx) => {
            const Icon = iconMap[sectionData.icon];
            return <motion.div key={idx} variants={itemVariants} className="bg-white border border-gray-200 rounded-3xl p-8 shadow-sm hover:shadow-md transition-shadow" data-id="element-292">
                  <div className="flex items-center mb-6 pb-4 border-b border-gray-100" data-id="element-293">
                    <div className="w-10 h-10 bg-brand-gold/10 rounded-xl flex items-center justify-center mr-4" data-id="element-294">
                      <Icon className="w-5 h-5 text-brand-gold" data-id="element-295" />
                    </div>
                    <h5 className="text-lg font-serif text-gray-900" data-id="element-296">
                      {sectionData.category}
                    </h5>
                  </div>
                  <ul className="space-y-4" data-id="element-297">
                    {sectionData.items.map((item, i) => <li key={i} className="flex justify-between items-center" data-id="element-298">
                        <span className="text-gray-500 text-sm" data-id="element-299">
                          {item.label}
                        </span>
                        <span className="font-medium text-gray-900" data-id="element-300">
                          {item.value}
                        </span>
                      </li>)}
                  </ul>
                </motion.div>;
          })}
          </motion.div>
        </div>

        <motion.p initial={{
        opacity: 0
      }} whileInView={{
        opacity: 1
      }} viewport={{
        once: true
      }} className="text-xs text-gray-400 text-center mt-12 font-light" data-id="element-301">
          *Real estate metrics sourced from {SOURCE} as of {LAST_UPDATED}.
          Neighborhood figures compiled from recent census and municipal data
          for the 91311 ZIP code.
        </motion.p>
      </div>
    </section>;
}